'use client'

import { useEffect } from 'react'
import { AlertTriangle, Clock } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'

interface TimerWarningModalProps {
  isOpen: boolean
  onClose: () => void
  onStopAndLog: () => void
  onContinue: () => void
  formattedTime: string
  issueSubject: string
}

export function TimerWarningModal({
  isOpen,
  onClose,
  onStopAndLog,
  onContinue,
  formattedTime,
  issueSubject
}: TimerWarningModalProps) {
  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <Card className="relative w-full max-w-md mx-4 border-amber-200 dark:border-amber-800/50 shadow-2xl">
        <CardContent className="pt-6">
          <div className="flex flex-col items-center text-center space-y-4">
            {/* Icon */}
            <div className="w-16 h-16 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
              <AlertTriangle className="h-8 w-8 text-amber-600 dark:text-amber-500" />
            </div>

            {/* Title */}
            <div className="space-y-2">
              <h3 className="text-xl font-semibold text-foreground">
                Timer is Running
              </h3>
              <p className="text-muted-foreground">
                You have an active time tracker running
              </p>
            </div>

            {/* Timer Display */}
            <div className="w-full p-4 bg-muted/50 rounded-lg border border-border/50">
              <div className="flex items-center justify-center gap-2 text-2xl font-mono font-bold text-foreground mb-2">
                <Clock className="h-6 w-6 text-amber-600 dark:text-amber-500" />
                {formattedTime}
              </div>
              <p className="text-sm text-muted-foreground truncate">
                {issueSubject}
              </p>
            </div>

            {/* Message */}
            <p className="text-sm text-muted-foreground">
              Would you like to stop the timer and log your time before leaving?
            </p>

            {/* Actions */}
            <div className="flex flex-col w-full gap-2 mt-4">
              <Button
                onClick={onStopAndLog}
                className="w-full"
                variant="default"
              >
                Stop & Log Time
              </Button>
              <Button
                onClick={onContinue}
                variant="outline"
                className="w-full"
              >
                Continue Without Logging
              </Button>
              <Button
                onClick={onClose}
                variant="ghost"
                className="w-full"
              >
                Cancel (Keep Timer Running)
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
